<template>
  <div class="new-studio" :class="{ 'new-studio--vscroll': gridStore.verticalScrollbarVisible }">
    <header data-testid="top-toolbar" class="new-studio__top" @pointerdown="onPanelClick">
      <NewStudioTopToolbar @view-in-room="$emit('viewInRoom')" />
    </header>
    <main class="new-studio__workspace">
      <aside data-testid="left-toolbar" class="new-studio__left" @pointerdown="onPanelClick">
        <NewStudioLeftToolbar />
      </aside>
      <div class="new-studio__center">
        <NewStudioColorPicker data-testid="top-color-picker" @pointerdown="onPanelClick" />
        <section ref="canvasHost" data-testid="canvas-host" class="new-studio__canvas">
          <ClientOnly>
            <CanvasGrid
              v-if="gridStore.gridPattern"
              return-to-add-on-blank-click
            />
          </ClientOnly>
          <p class="new-studio__selection-status" data-testid="selection-status" aria-live="polite">
            {{ selectionStatus }}
          </p>
          <NewStudioEngravingChooser />
          <div class="new-studio__overlay-anchor new-studio__overlay-anchor--controls">
            <div data-testid="floating-grid-controls" class="new-studio__canvas-controls-capture">
              <NewStudioCanvasControls />
            </div>
          </div>
          <div class="new-studio__overlay-anchor">
            <div data-testid="selection-actions" class="new-studio__selection-actions-capture">
              <NewStudioSelectionActions />
            </div>
          </div>
        </section>
      </div>
      <aside data-testid="right-panel" class="new-studio__right" @pointerdown="onPanelClick">
        <NewStudioPropertiesPanel />
      </aside>
    </main>
    <div data-testid="modal-host">
      <NewStudioModalHost
        :ar-open="arModalOpen"
        :ar-model-url="arModelUrl"
        :ar-page-url="arPageUrl"
        :ar-loading="arLoading"
        :ar-error="arError"
        :ar-auto-launch="arAutoLaunch"
        @close-ar="$emit('closeAr')"
        @retry-ar="$emit('retryAr')"
      />
    </div>
    <ClientOnly>
      <Tutorial
        :class="{ 'new-studio__tutorial--hidden': modalBlockingTutorial }"
        :aria-hidden="modalBlockingTutorial ? 'true' : undefined"
      />
      <Loading v-if="loadingStore.isLoading" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { createCanvasHostResizeAdapter } from './logic'

const props = defineProps<{
  arModalOpen: boolean
  arModelUrl: string | null
  arPageUrl: string | null
  arLoading: boolean
  arError: string | null
  arAutoLaunch: boolean
}>()

defineEmits<{
  closeAr: []
  retryAr: []
  viewInRoom: []
}>()

const gridStore = useGridStore()
const loadingStore = useLoadingStore()
const modalStore = useModalStore()
const modalBlockingTutorial = computed(
  () => modalStore.isAnyVisible || props.arModalOpen,
)
const canvasHost = useTemplateRef<HTMLElement>('canvasHost')
const selectionStatus = computed(() => {
  const selected = gridStore.selectedItemsOnGrid
  if (!selected.length) return 'No tiles selected'
  const metadata = selected[0]?.metadata || selected[0]?.attrs?.metadata
  const shape = metadata?.shape_id || metadata?.shape_label || 'tile'
  const color = metadata?.color_label || 'unknown color'
  return `${selected.length} selected: ${shape} in ${color}`
})
let stopCanvasHostResize: (() => void) | undefined

const onPanelClick = (e: PointerEvent) => {
  const target = e.target as HTMLElement
  // Don't switch tool if clicking on interactive elements
  if (target.closest('button, input, label, [data-shape-root], [data-shape-id], .color-swatch, .color-picker')) return
  gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT)
}

onMounted(() => {
  if (canvasHost.value) {
    stopCanvasHostResize = createCanvasHostResizeAdapter(canvasHost.value)
  }
})

onUnmounted(() => stopCanvasHostResize?.())
</script>

<style scoped>
@import './tokens.css';

.new-studio {
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-rows: var(--studio-toolbar-height) minmax(0, 1fr);
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  overscroll-behavior: none;
  color: var(--studio-text);
  background: var(--studio-surface);
  font-family: var(--studio-font-family);
}

.new-studio__workspace {
  display: grid;
  grid-template-columns: var(--studio-left-rail-width) minmax(0, 1fr) var(--studio-right-panel-width);
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
}

.new-studio__top,
.new-studio__left,
.new-studio__right {
  background: var(--studio-surface);
}

.new-studio__left {
  min-height: 0;
  overflow: hidden;
}

.new-studio__right {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.new-studio__center {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.new-studio__canvas {
  position: relative;
  flex: 1 1 0%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--studio-canvas-surface);
}

.new-studio__selection-status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.new-studio__overlay-anchor {
  position: absolute;
  z-index: 3;
  inset: 0;
  pointer-events: none;
}

/* Lift the entire controls stacking context so its popover clears selection actions. */
.new-studio__overlay-anchor--controls {
  z-index: 4;
}

.new-studio__canvas-controls-capture,
.new-studio__selection-actions-capture {
  position: absolute;
  pointer-events: none;
}

.new-studio__canvas-controls-capture {
  top: 16px;
  left: 16px;
  width: 108px;
  height: 453px;
  padding: 7px 10px 15px;
}

.new-studio__selection-actions-capture {
  bottom: 7px;
  left: calc(50% + .5px);
  width: 373px;
  height: 98px;
  padding: 7px 10px 15px;
  transform: translateX(-50%);
}

.new-studio__canvas-controls-capture :deep(.canvas-controls),
.new-studio__selection-actions-capture :deep(.selection-actions) {
  position: static;
  transform: none;
}

.new-studio :deep(.studioTutorial) {
  top: calc(var(--studio-toolbar-height) + 125px);
  right: var(--studio-right-panel-width);
  transition: right 0.15s ease;
}

/* When the canvas vertical scrollbar is visible, shift the tutorial arrow
   left by the scrollbar track width (14px) so it isn't covered. */
.new-studio--vscroll :deep(.studioTutorial) {
  right: calc(var(--studio-right-panel-width) + 14px);
}

.new-studio :deep(.studioTutorial.new-studio__tutorial--hidden) {
  visibility: hidden;
  pointer-events: none;
}

.new-studio :deep(.studioTutorial.tutorialFinished) {
  width: 26px;
}

.new-studio :deep(.studioTutorial.tutorialFinished .tutorialBody) {
  display: none;
}

.new-studio :deep(.studioTutorial.tutorialFinished .closer) {
  width: 26px;
  height: 54px;
  margin: 0;
  background: #313558;
  border-radius: 9px 0 0 9px;
  color: #fff;
  font-size: 14px;
  box-shadow: -2px 2px 4px rgba(47, 127, 236, 0.32);
}
</style>
